// Fennest Constants

export const TRIAL_DURATION_DAYS = 30;

/**
 * Canonical public app URL for share links (family invites, goal invites, etc).
 * Always prefers the production domain so links don't accidentally point to a
 * Lovable preview/sandbox URL.
 */
export const PUBLIC_APP_URL = 'https://fennest.com';

export function getPublicAppUrl(): string {
  if (typeof window === 'undefined') return PUBLIC_APP_URL;
  const host = window.location.hostname;
  // Use current origin only when running locally; otherwise force the production domain.
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return window.location.origin;
  }
  return PUBLIC_APP_URL;
}

export const PLANS = {
  free: {
    type: 'free' as const,
    displayName: 'Free',
    maxBudgets: 1,
    maxActiveGoals: 1,
    hasSharedGoals: false,
    hasOCR: false,
    hasAICoach: false,
    hasPetSystem: true,
    includedUsers: 1,
    price: 0,
    valueBullets: [
      'Track your spending',
      'Build your streak',
      'Grow your Fennie',
      '1 savings goal',
      'Recent journey history (last 20 events)',
    ],
  },
  premium: {
    type: 'premium' as const,
    displayName: 'Individual',
    maxBudgets: 1,
    maxActiveGoals: 999,
    hasSharedGoals: true,
    hasOCR: true,
    hasAICoach: true,
    hasPetSystem: true,
    includedUsers: 1,
    annualPrice: 79,
    monthlyAnchor: 9.99,
    // Keep legacy fields for backward compat
    price: 9.99,
    yearlyPrice: 79,
    launchPrice: 9.99,
    launchYearlyPrice: 79,
    valueBullets: [
      'Everything in Free',
      'AI spending insights - spot patterns you miss',
      'AI coaching - personalized nudges and guidance',
      'Receipt scanning - snap and track instantly',
      'Voice assistant - log expenses by talking',
      'Complete Fennie journey - every evolution, every milestone',
      'Unlimited goals',
    ],
  },
  family: {
    type: 'family' as const,
    displayName: 'Family',
    maxBudgets: 1,
    maxActiveGoals: 999,
    hasSharedGoals: true,
    hasOCR: true,
    hasAICoach: true,
    hasPetSystem: true,
    includedUsers: 3,
    annualPrice: 149,
    monthlyAnchor: 19.99,
    // Keep legacy fields for backward compat
    price: 19.99,
    yearlyPrice: 149,
    launchPrice: 19.99,
    launchYearlyPrice: 149,
    valueBullets: [
      'Everything in Individual',
      'You plus up to 3 members (4 people)',
      'Shared accountability',
      'See who\u2019s staying consistent',
    ],
  }
} as const;

export const ADDITIONAL_USER_PRICE = 1.00;


export const DEFAULT_CATEGORIES = [
  { name: 'Food & Dining', icon: '🍕', color: 'text-orange-500' },
  { name: 'Groceries', icon: '🛒', color: 'text-lime-500' },
  { name: 'Transportation', icon: '🚗', color: 'text-blue-500' },
  { name: 'Shopping', icon: '🛍️', color: 'text-purple-500' },
  { name: 'Entertainment', icon: '🎬', color: 'text-pink-500' },
  { name: 'Subscriptions', icon: '📺', color: 'text-indigo-500' },
  { name: 'Bills & Utilities', icon: '⚡', color: 'text-yellow-500' },
  { name: 'Healthcare', icon: '🏥', color: 'text-red-500' },
  { name: 'Education', icon: '📚', color: 'text-green-500' },
  { name: 'Travel', icon: '✈️', color: 'text-cyan-500' },
  { name: 'Home & Garden', icon: '🏠', color: 'text-emerald-500' },
  { name: 'Loans', icon: '🏦', color: 'text-amber-500' },
  { name: 'Credit Cards', icon: '💳', color: 'text-rose-500' },
  { name: 'Other', icon: '📝', color: 'text-gray-500' }
];

export const PET_STAGES = {
  egg: { emoji: '🥚', name: 'Egg', minDays: 0 },
  baby: { emoji: '🐣', name: 'Baby', minDays: 3 },
  teen: { emoji: '🐤', name: 'Teen', minDays: 7 },
  adult: { emoji: '🐦', name: 'Adult', minDays: 30 },
  master: { emoji: '🦅', name: 'Master', minDays: 90 }
} as const;

export const PET_MOODS = {
  happy: '😊',
  neutral: '😐',
  sad: '😢',
  excited: '🤩'
} as const;

export const MILESTONE_REWARDS = [
  { days: 7, name: 'First Week', description: 'You\'re building habits!', reward: 'hat_1' },
  { days: 14, name: 'Two Weeks', description: 'Consistency is key!', reward: 'sunglasses_1' },
  { days: 30, name: 'One Month', description: 'You\'re on fire!', reward: 'background_1' },
  { days: 60, name: 'Two Months', description: 'Financial discipline!', reward: 'animation_1' },
  { days: 90, name: 'Three Months', description: 'Master budgeter!', reward: 'pet_evolution' }
];

export const COACH_TONES = {
  encouraging: {
    name: 'Encouraging',
    description: 'Positive and supportive coaching style',
    example: '"Great job staying on track! You\'re doing amazing!"'
  },
  'tough-love': {
    name: 'Tough Love',
    description: 'Direct and challenging coaching style',
    example: '"Time to get serious about your spending habits."'
  },
  sarcastic: {
    name: 'Sarcastic',
    description: 'Witty and humorous coaching style',
    example: '"Another coffee purchase? At least it\'s not a yacht."'
  }
} as const;

export const RECURRING_FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' }
] as const;

export const CURRENCIES = [
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'COP', symbol: '$', name: 'Colombian Peso' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  { code: 'QAR', symbol: 'ر.ق', name: 'Qatari Riyal' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
] as const;

export const NOTIFICATION_TYPES = {
  GOAL_CONTRIBUTION: 'goal_contribution',
  GOAL_REACTION: 'goal_reaction',
  COACH_ALERT: 'coach_alert',
  MILESTONE: 'milestone',
  BUDGET_ALERT: 'budget_alert',
  MONTHLY_RESET: 'monthly_reset'
} as const;

export const API_ENDPOINTS = {
  OCR: '/api/ocr',
  AI_COACH: '/api/coach',
  NOTIFICATIONS: '/api/notifications',
  STRIPE_CHECKOUT: '/api/stripe/checkout',
  STRIPE_PORTAL: '/api/stripe/portal'
} as const;

// Fennie Mascot System
export const FENNIE_MOODS = {
  neutral: { name: 'Neutral', description: 'Calm and focused' },
  happy: { name: 'Happy', description: 'Celebrating your progress!' },
  sad: { name: 'Sad', description: 'Needs some encouragement' },
  proud: { name: 'Proud', description: 'Impressed by your achievements!' }
} as const;

export const FENNIE_ACCESSORIES = {
  scarf: { name: 'Cozy Scarf', description: 'For winter budgeting sessions', unlockDays: 7 },
  glasses: { name: 'Smart Glasses', description: 'For serious financial planning', unlockDays: 30 },
  bowtie: { name: 'Formal Bow Tie', description: 'For celebrating milestones', unlockDays: 60 }
} as const;

export const FIRESTORE_COLLECTIONS = {
  USERS: 'users',
  BUDGETS: 'budgets',
  EXPENSES: 'expenses',
  GOALS: 'goals',
  GOAL_ACTIVITIES: 'goalActivities',
  PET_STATUS: 'petStatus',
  COACH_MESSAGES: 'coachMessages',
  NOTIFICATIONS: 'notifications'
} as const;

// Expense Categories for forms
export const EXPENSE_CATEGORIES = [
  { id: 'food', label: 'Food & Dining', emoji: '🍕' },
  { id: 'groceries', label: 'Groceries', emoji: '🛒' },
  { id: 'transport', label: 'Transportation', emoji: '🚗' },
  { id: 'shopping', label: 'Shopping', emoji: '🛍️' },
  { id: 'entertainment', label: 'Entertainment', emoji: '🎬' },
  { id: 'subscriptions', label: 'Subscriptions', emoji: '📺' },
  { id: 'utilities', label: 'Bills & Utilities', emoji: '⚡' },
  { id: 'health', label: 'Healthcare', emoji: '🏥' },
  { id: 'education', label: 'Education', emoji: '📚' },
  { id: 'travel', label: 'Travel', emoji: '✈️' },
  { id: 'home', label: 'Home & Garden', emoji: '🏠' },
  { id: 'loans', label: 'Loans', emoji: '🏦' },
  { id: 'credit_cards', label: 'Credit Cards', emoji: '💳' },
  { id: 'other', label: 'Other', emoji: '📝' },
] as const;
